"use client";

import type { IeltsModule, IeltsTaskType } from "@/lib/exams/ielts/criteria";
import {
  type Stimulus,
  serialiseStimulusForMarking,
  stimuliFor,
  getStimulus,
} from "@/lib/exams/ielts/stimulus";
import type { ExamMarkConfig } from "@/lib/exams/markConfig";
import { IeltsTask1Chart } from "./IeltsTask1Chart";

export const IELTS_MODULES = [
  { value: "academic", label: "Academic" },
  { value: "general", label: "General Training" },
] as const;

export const IELTS_TASKS = [
  { value: "task1", label: "Task 1" },
  { value: "task2", label: "Task 2" },
] as const;

export const IELTS_TARGET_BANDS = [5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9] as const;

// The single source of the marker's context string (initial mark AND re-marks).
// Embeds module/task/target band, the human task instruction, and the marking
// ground-truth (chart data table / required bullets); names the stimulusId for
// the model to echo so the result re-hydrates the chart on resume.
export const buildIeltsMarkPrefix = (
  module: IeltsModule,
  taskType: IeltsTaskType,
  targetBand: number | null,
  stimulus: Stimulus,
): string => {
  const groundTruth = serialiseStimulusForMarking(stimulus);
  return [
    `MODULE: ${module}`,
    `TASK: ${taskType}`,
    `TARGET BAND (coaching tone only — must NOT affect marks): ${targetBand ?? "none stated"}`,
    `stimulusId (echo this back verbatim): ${stimulus.id}`,
    `TASK INSTRUCTION: ${stimulus.promptText}`,
    groundTruth ? `\n${groundTruth}` : "",
    "",
  ].join("\n");
};

interface IeltsMarkContextProps {
  module: IeltsModule;
  taskType: IeltsTaskType;
  targetBand: number;
  onModuleChange: (m: IeltsModule) => void;
  onTaskChange: (t: IeltsTaskType) => void;
  onTargetBandChange: (b: number) => void;
}

const selectClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-white";

export function IeltsMarkContext({
  module,
  taskType,
  targetBand,
  onModuleChange,
  onTaskChange,
  onTargetBandChange,
}: IeltsMarkContextProps) {
  return (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-[120px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Module</label>
        <select
          value={module}
          onChange={(e) => onModuleChange(e.target.value as IeltsModule)}
          className={selectClass}
        >
          {IELTS_MODULES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[100px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Task</label>
        <select
          value={taskType}
          onChange={(e) => onTaskChange(e.target.value as IeltsTaskType)}
          className={selectClass}
        >
          {IELTS_TASKS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[100px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Target band</label>
        <select
          value={targetBand}
          onChange={(e) => onTargetBandChange(Number(e.target.value))}
          className={selectClass}
        >
          {IELTS_TARGET_BANDS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mark-form config (drives the generic mark page + resume view)
// ---------------------------------------------------------------------------

export interface IeltsSelState {
  module: IeltsModule;
  taskType: IeltsTaskType;
  targetBand: number;
  stimulus: Stimulus | null;
}

const firstStimulus = (module: IeltsModule, taskType: IeltsTaskType): Stimulus | null =>
  stimuliFor(module, taskType)[0] ?? null;

function IeltsSelectors({
  state,
  onChange,
}: {
  state: IeltsSelState;
  onChange: (next: IeltsSelState) => void;
}) {
  // Selector changes re-pick the first stimulus for the new variant; "New prompt"
  // rotates within the current variant. No effects — state is fully controlled.
  const setModule = (module: IeltsModule) =>
    onChange({ ...state, module, stimulus: firstStimulus(module, state.taskType) });
  const setTask = (taskType: IeltsTaskType) =>
    onChange({ ...state, taskType, stimulus: firstStimulus(state.module, taskType) });
  const shuffle = () => {
    const list = stimuliFor(state.module, state.taskType);
    if (list.length === 0) return;
    const idx = state.stimulus ? list.findIndex((s) => s.id === state.stimulus!.id) : -1;
    onChange({ ...state, stimulus: list[(idx + 1) % list.length] });
  };
  return (
    <div className="space-y-3">
      <IeltsMarkContext
        module={state.module}
        taskType={state.taskType}
        targetBand={state.targetBand}
        onModuleChange={setModule}
        onTaskChange={setTask}
        onTargetBandChange={(targetBand) => onChange({ ...state, targetBand })}
      />
      {state.stimulus && (
        <div className="space-y-2 rounded-lg border border-brand-soft bg-brand-surface p-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs font-semibold tracking-wide text-brand uppercase">Your task</p>
            <button
              type="button"
              onClick={shuffle}
              className="text-xs text-brand underline hover:opacity-70"
            >
              New prompt
            </button>
          </div>
          <p className="text-sm whitespace-pre-wrap text-brand">{state.stimulus.promptText}</p>
          {state.stimulus.bulletPoints && (
            <ul className="list-inside list-disc text-sm text-brand">
              {state.stimulus.bulletPoints.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
          {state.stimulus.chartSpec && <IeltsTask1Chart spec={state.stimulus.chartSpec} />}
        </div>
      )}
    </div>
  );
}

export const ieltsMarkConfig: ExamMarkConfig<IeltsSelState> = {
  defaultState: {
    module: "academic",
    taskType: "task1",
    targetBand: 7,
    stimulus: firstStimulus("academic", "task1"),
  },
  Selectors: IeltsSelectors,
  promptText: (s) => s.stimulus?.promptText ?? null,
  handoffKeys: ["ieltsModule", "ieltsTask", "ieltsTargetBand", "ieltsStimulusId"],
  buildHandoff: (s): Record<string, string> =>
    s.stimulus
      ? {
          ieltsModule: s.module,
          ieltsTask: s.taskType,
          ieltsTargetBand: String(s.targetBand),
          ieltsStimulusId: s.stimulus.id,
        }
      : {},
  buildPrefix: (h, basePrompt) => {
    // `moduleId` (not `module`) — Next's no-assign-module-variable rule.
    const moduleId = h.ieltsModule as IeltsModule | null;
    const taskType = h.ieltsTask as IeltsTaskType | null;
    const stimulusId = h.ieltsStimulusId;
    if (moduleId && taskType && stimulusId) {
      const stimulus = getStimulus(moduleId, taskType, stimulusId);
      if (stimulus) {
        const targetBand = h.ieltsTargetBand ? Number(h.ieltsTargetBand) : null;
        return buildIeltsMarkPrefix(moduleId, taskType, targetBand, stimulus);
      }
    }
    return basePrompt;
  },
};
